import { ThemeEvents, QuantityBreaksUpdatedEvent, VariantUpdatedEvent } from '@theme/events';

/**
 * Format Picker — sélecteur de variantes en cards.
 *
 * Chaque card binde un FORMAT (ex: "5 Eyes Patchs") et expose 2 IDs de variante :
 *   - data-variant-id-ou    : la variante "Achat unique"
 *   - data-variant-id-abo   : la variante "Abonnement"
 *
 * Au clic sur une card ou au toggle du radio S'abonner, le JS sélectionne
 * la variante OU ou Abo correspondante et met à jour le form + les composants.
 */
class FormatPicker extends HTMLElement {
  constructor() {
    super();
    this.handleCardClick = this.handleCardClick.bind(this);
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleSubscriptionRadioChange = this.handleSubscriptionRadioChange.bind(this);
  }

  connectedCallback() {
    this.cards = this.querySelectorAll('[data-format-card="true"]');
    this.cards.forEach((card) => {
      card.addEventListener('click', this.handleCardClick);
    });
    this.syncLegacyQuantityBreakClass();
    document.addEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdate);

    // Écoute les changements de mode (Achat unique / S'abonner) pour switcher la variante
    document.addEventListener('change', this.handleSubscriptionRadioChange);
  }

  disconnectedCallback() {
    this.cards?.forEach((card) => {
      card.removeEventListener('click', this.handleCardClick);
    });
    document.removeEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdate);
    document.removeEventListener('change', this.handleSubscriptionRadioChange);
  }

  /**
   * Retourne true si S'abonner est actuellement coché dans le bloc subscription.
   */
  isSubscriptionSelected() {
    const subOption = document.querySelector('subscription-component [data-ref="subscription-option"]');
    return !!(subOption && subOption.checked);
  }

  /**
   * Retourne l'ID de variante correct (OU ou Abo) pour une card donnée,
   * en fonction de l'état S'abonner.
   */
  pickVariantForCard(card) {
    const isSub = this.isSubscriptionSelected();
    const variantId = isSub
      ? (card.dataset.variantIdAbo || card.dataset.variantId)
      : (card.dataset.variantIdOu || card.dataset.variantId);
    return variantId;
  }

  /**
   * Retourne la card actuellement sélectionnée (si une).
   */
  getSelectedCard() {
    for (const c of this.cards) {
      if (c.classList.contains('format-card--selected')) return c;
    }
    return null;
  }

  handleCardClick(event) {
    // Éviter double-trigger (label contient un input radio qui déclencherait click aussi)
    if (event.target.tagName === 'INPUT') return;

    const card = event.currentTarget;
    if (card.classList.contains('format-card--disabled')) return;

    const variantId = this.pickVariantForCard(card);
    if (!variantId) return;

    this.selectVariant(variantId, card);
  }

  /**
   * Déclenché quand un radio change sur la page — si c'est un radio subscription,
   * on switche la variante de la card actuellement sélectionnée vers son pendant OU/Abo.
   */
  handleSubscriptionRadioChange(event) {
    const target = event.target;
    if (!target || target.tagName !== 'INPUT' || target.type !== 'radio') return;

    const dataRef = target.getAttribute('data-ref');
    if (dataRef !== 'subscription-option' && dataRef !== 'one-time-option') return;

    // IMPORTANT: On met à jour data-default-selected sur la <subscription-component>
    // pour que subscription.js, qui re-run #initDefaultSelection après le variant update,
    // lise le bon mode. Sinon il remet Achat unique par défaut (bug n°1).
    const subComponent = document.querySelector('subscription-component');
    if (subComponent) {
      subComponent.dataset.defaultSelected = (dataRef === 'subscription-option') ? 'subscription' : 'one_time';
    }

    // Trouve la card active et switch vers OU ou Abo selon le nouveau mode
    const selectedCard = this.getSelectedCard();
    if (!selectedCard) return;

    const newVariantId = this.pickVariantForCard(selectedCard);
    const currentFormId = document.querySelector('form[action$="/cart/add"] [name="id"]')?.value;
    if (!newVariantId || String(newVariantId) === String(currentFormId)) return;

    this.selectVariant(newVariantId, selectedCard);
  }

  selectVariant(variantId, clickedCard) {
    // 1) UI cards (on garde la class --selected sur la card, peu importe OU/Abo)
    this.cards.forEach((c) => {
      c.classList.remove('format-card--selected');
      c.classList.remove('quantity-break--selected');
      const radio = c.querySelector('[data-ref="format-card-input"]');
      if (radio) radio.checked = false;
    });
    clickedCard.classList.add('format-card--selected');
    clickedCard.classList.add('quantity-break--selected');
    const radio = clickedCard.querySelector('[data-ref="format-card-input"]');
    if (radio) radio.checked = true;

    // 2a) Input hidden interne au format-picker
    const pickerHiddenId = this.querySelector('[data-ref="format-picker-variant-id"]');
    if (pickerHiddenId) pickerHiddenId.value = variantId;

    // 2b) Form produit : update select[name=id] / input[name=id]
    const productForm = this.closest('[data-ref="product-section-content"]')?.querySelector('form[action$="/cart/add"]')
      || document.querySelector('form[action$="/cart/add"]');

    if (productForm) {
      const allVariantInputs = productForm.querySelectorAll('select[name="id"], input[name="id"]');
      if (allVariantInputs.length > 0) {
        allVariantInputs.forEach((el) => {
          el.value = variantId;
          el.dispatchEvent(new Event('change', { bubbles: true }));
        });
      } else {
        let hidden = productForm.querySelector('input[name="id"][data-format-picker-generated]');
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'id';
          hidden.setAttribute('data-format-picker-generated', 'true');
          productForm.appendChild(hidden);
        }
        hidden.value = variantId;
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // 3) Fetch + dispatch VariantUpdatedEvent (prix, ATC, subscription se rafraîchissent)
    this.fetchAndDispatchVariantUpdate(variantId, productForm);

    // 4) QuantityBreaksUpdatedEvent pour la logique tier qty_1/2/3 du subscription
    this.dispatchEvent(new QuantityBreaksUpdatedEvent());

    // 5) URL sans reload
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    } catch (_) {}
  }

  async fetchAndDispatchVariantUpdate(variantId, productForm) {
    try {
      const productHandle = productForm?.dataset.productHandle
        || window.location.pathname.split('/products/')[1]?.split(/[?\/]/)[0];

      if (!productHandle) return;

      const resp = await fetch(
        `/products/${productHandle}?variant=${variantId}`,
        { headers: { 'Accept': 'text/html' } }
      );
      if (!resp.ok) return;

      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      let variant = { id: Number(variantId) };
      const variantScript = doc.querySelector('variant-picker script[type="application/json"]');
      if (variantScript) {
        try { variant = JSON.parse(variantScript.textContent); } catch (_) {}
      }

      const sectionId = this.dataset.sectionId
        || productForm?.dataset.sectionId
        || '';

      const variantEvent = new VariantUpdatedEvent(variant, sectionId, {
        html: doc,
        productId: productForm?.dataset.productId || '',
        sectionId,
      });
      this.dispatchEvent(variantEvent);
    } catch (e) {
      console.warn('format-picker: fetchAndDispatchVariantUpdate failed', e);
    }
  }

  handleVariantUpdate(event) {
    const variant = event.detail?.variant;
    if (!variant) return;

    // Une card est "selected" si son OU ou Abo ID correspond à la variante courante
    const currentId = String(variant.id);
    this.cards.forEach((c) => {
      const ouId = String(c.dataset.variantIdOu || '');
      const aboId = String(c.dataset.variantIdAbo || '');
      const matches = ouId === currentId || aboId === currentId;
      c.classList.toggle('format-card--selected', matches);
      c.classList.toggle('quantity-break--selected', matches);
      const radio = c.querySelector('[data-ref="format-card-input"]');
      if (radio) radio.checked = matches;
    });

    const pickerHiddenId = this.querySelector('[data-ref="format-picker-variant-id"]');
    if (pickerHiddenId) pickerHiddenId.value = variant.id;
  }

  syncLegacyQuantityBreakClass() {
    this.cards.forEach((c) => {
      if (c.classList.contains('format-card--selected')) {
        c.classList.add('quantity-break--selected');
      }
    });
  }
}

customElements.define('format-picker', FormatPicker);
